import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import { camelCase, get } from 'lodash';
import { useCallback, useMemo } from 'react';

import { type InputComponent } from '..';
import { normalizeSize } from '../helpers';

export interface InputQuantityProps extends InputComponent {
  min?: number;
  max?: number;
  step?: number;
}

export function InputQuantity({
  className,
  disabled,
  hidden,
  hint,
  label,
  nameOverride,
  readOnly,
  required,
  step = 1,
  min = -1 * Number.MAX_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  size: propSize = 'small',
}: InputQuantityProps) {
  const { errors, values, setFieldValue, setFieldTouched } =
    useFormikContext<Record<string, number | string>>();

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const error = useMemo(() => get(errors, name), [errors, name]);

  const rawValue = get(values, name);
  const quantity: number =
    (rawValue as unknown) === '' ? 0 : Number(rawValue) || 0;

  // This is necessary to support non integer steps.
  // 1. Modulo is not valid for floating points.
  // 2. If we are not doing integer math then we need to round so we don't
  //    have weird looking numbers. 1.499999999999 is 1.5 if step is 0.1
  const stepsDecimalPlaces = useMemo(
    () => step?.toString()?.split('.')?.[1]?.length || 0,
    [step],
  );

  const changeValue = useCallback(
    (type: 'INCREMENT' | 'DECREMENT') => {
      if (disabled) return;

      const multiplier = type === 'INCREMENT' ? 1 : -1;
      const updated = Math.max(
        min,
        Math.min(max, quantity + step * multiplier),
      );

      setFieldTouched(name, true);
      // The quantity value is rounded to the stepsDecimalPlaces so we don't have goofy looking numbers
      setFieldValue(
        name,
        parseFloat(updated.toFixed(stepsDecimalPlaces)),
        true,
      );
    },
    [
      disabled,
      quantity,
      min,
      max,
      step,
      stepsDecimalPlaces,
      setFieldTouched,
      setFieldValue,
      name,
    ],
  );

  const handleQuantityInputChange = useCallback(
    (value: string) => {
      const cleanedValue = value.replace(/[^\d.]/g, '');

      // Allow empty string (for clearing), otherwise convert to number
      const parsedValue = cleanedValue === '' ? '' : Number(cleanedValue);

      setFieldTouched(name, true, false);
      setFieldValue(name, parsedValue, true);
    },
    [setFieldTouched, setFieldValue, name],
  );

  const currentValue = get(values, name);
  const displayValue =
    currentValue === '' || currentValue === undefined ? '' : currentValue;
  const size = normalizeSize(propSize);
  const formControlSize = size === 'large' ? 'medium' : size;

  return (
    <FormControl size={formControlSize} fullWidth className={className}>
      <InputLabel>{label}</InputLabel>
      <OutlinedInput
        sx={{
          minWidth: 120,
          '& input': { textAlign: 'center' },
          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
            {
              WebkitAppearance: 'none',
              margin: 0,
            },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
        }}
        value={displayValue}
        onChange={(e) => handleQuantityInputChange(e.target.value)}
        disabled={disabled}
        hidden={hidden}
        readOnly={readOnly}
        fullWidth
        required={required && !readOnly}
        label={label}
        name={name}
        type="number"
        // `type="number"` alone gets a phone's full keyboard on its numbers
        // page. `inputMode` asks for the digit pad — with a decimal point only
        // when the step has one. No minus key is no loss: typed input is
        // stripped to digits and a point anyway.
        inputProps={{
          inputMode: stepsDecimalPlaces > 0 ? 'decimal' : 'numeric',
          pattern: stepsDecimalPlaces > 0 ? undefined : '[0-9]*',
        }}
        endAdornment={
          <InputAdornment
            position="end"
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            <IconButton
              onClick={() => changeValue('INCREMENT')}
              edge="end"
              disabled={disabled || readOnly}
            >
              <Add />
            </IconButton>
          </InputAdornment>
        }
        startAdornment={
          <InputAdornment
            position="start"
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            <IconButton
              onClick={() => changeValue('DECREMENT')}
              edge="start"
              disabled={disabled || readOnly}
            >
              <Remove />
            </IconButton>
          </InputAdornment>
        }
      />
      {(error || hint) && (
        <FormHelperText error={!!error}>{error || hint}</FormHelperText>
      )}
    </FormControl>
  );
}
